import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {Checkbox} from "@khanacademy/wonder-blocks-form";
import {sizing} from "@khanacademy/wonder-blocks-tokens";
import {BodyText, Heading} from "@khanacademy/wonder-blocks-typography";

import {Section} from "../components/section/section.tsx";
import type {AccessibilitySettingsData} from "../types/account-settings-types.ts";

type Props = {
    disabled: boolean;
    onSettingsChange: (settings: Partial<AccessibilitySettingsData>) => void;
    footer?: React.ReactNode;
} & AccessibilitySettingsData;

export const AccessibilitySettingsForm: React.FC<Props> = ({
    hideVisual,
    prefersReducedMotion,
    noColorInVideos,
    disabled,
    soundOn,
    onSettingsChange,
    footer,
}) => {
    return (
        <Section
            legendContent={
                <View style={styles.legendContent}>
                    <Heading tag="h2" size="large" weight="bold">
                        {t`Accessibility`}
                    </Heading>
                    <BodyText>
                        {t`Choose options that support how you see, hear, or interact with content.`}
                    </BodyText>
                </View>
            }
            children={
                <View
                    aria-label={t`Accessibility settings`}
                    style={styles.wrapper}
                >
                    <View style={{gap: sizing.size_120}}>
                        <Checkbox
                            checked={hideVisual}
                            id="hide-visual"
                            onChange={(hideVisual: boolean) =>
                                onSettingsChange({hideVisual})
                            }
                            disabled={disabled}
                            label={t`Hide visually-dependent content`}
                        />
                        <Checkbox
                            label={t`Reduce motion and animations`}
                            id="prefers-reduced-motion"
                            checked={prefersReducedMotion}
                            onChange={(prefersReducedMotion: boolean) =>
                                onSettingsChange({prefersReducedMotion})
                            }
                            disabled={disabled}
                        />
                        <Checkbox
                            label={t`Remove color from videos`}
                            id="no-color-in-videos"
                            checked={noColorInVideos}
                            onChange={(noColorInVideos: boolean) =>
                                onSettingsChange({noColorInVideos})
                            }
                            disabled={disabled}
                        />
                        <Checkbox
                            label={t`Mute sound effects`}
                            id="sound"
                            checked={!soundOn}
                            onChange={(muted: boolean) =>
                                onSettingsChange({soundOn: !muted})
                            }
                            disabled={disabled}
                        />
                    </View>
                </View>
            }
            footer={footer}
        />
    );
};

const styles = StyleSheet.create({
    wrapper: {
        gap: sizing.size_240,
    },
    legendContent: {
        gap: sizing.size_160,
        marginBlockEnd: sizing.size_120,
    },
});

export default AccessibilitySettingsForm;
